import React, { useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Chip } from '@mui/material';
import UserList from '../../components/user-management/UserList';
import DeletedAccountList from '../../components/deleted-accounts/DeletedAccountList';
import SearchFilter from '../../components/analytics-and-insights/SearchFilter';
import { setStatsTypeFilter, setResetFilter, setIosUserFilter, setDateFilter } from '../app-container/reducer/filterSlice';
import { STATUS_LABEL as MEMBER_STATUS_LABEL } from '../../utils/memberStatus';

/**
 * Channel Attribution — members for one attribution node.
 *
 * Opened from a "View →" link in the Channel Attribution table. Renders the SAME
 * `UserList` component as User Management / Bot Management — identical columns,
 * column toggles, search, row actions and pagination — scoped to the attribution
 * node by seeding the shared Redux filter with `source=attribution` + the node.
 * getUsers' attribution mode then applies buildNodeMatch, so the member count
 * matches the tree row this page was opened from (parity-verified).
 *
 * Exception: the "Deleted" bucket. Deleted profiles live in a SEPARATE collection
 * (the delete-log), not the live users collection UserList reads — so for
 * status==='deleted' we render `DeletedAccountList` instead. It reads the same
 * seeded Redux filter and its API forwards the node params (source==='attribution'),
 * so getDeletedAccounts scopes the log to this node and the count still matches.
 *
 * The node identity lives in the URL query (channel/campaign/adset/creative +
 * gender/platform/from/to). We seed it on mount and CLEAR it on unmount, because
 * setResetFilter() does not touch these keys — otherwise the node would leak into
 * User Management on the next visit.
 *
 * Filtering (2026-09-23): the page renders User Management's full `SearchFilter` bar, plus
 * the node itself as removable chips. Clearing a chip rewrites the URL, which re-seeds the
 * filter — so dropping `creative` widens the list to its ad set, dropping `campaign` widens
 * to the channel, and so on up to all members. Note this means the list NO LONGER always
 * equals the tree cell it was opened from: narrowing or widening here is deliberate, and the
 * way back to a matching count is to re-open the cell from the tree.
 */
const NODE_KEYS = ['channel', 'campaign', 'adset', 'creative', 'platform', 'from', 'to', 'status'];

// The attribution hierarchy, outermost first. Clearing one level must also clear every
// level BELOW it: "campaign X" with no channel is still a real filter, but "creative Y"
// with no campaign is not the node anyone drilled into — it would silently match that
// creative name in every other campaign (ad-set and creative names are reused heavily).
const LEVELS = ['channel', 'campaign', 'adset', 'creative'];
const LEVEL_LABEL = { channel: 'Channel', campaign: 'Campaign', adset: 'Ad set', creative: 'Creative' };

// Pretty label for the account-status bucket a number was clicked from. The seven
// statuses + Active (Approved + Hidden) come from utils/memberStatus.js; the rest are
// this screen's overlay columns.
const STATUS_LABEL = {
  signups: 'Sign ups', completed: 'Completed (Ads Event)', filled: 'Profile 100% complete',
  ...MEMBER_STATUS_LABEL,
  subscribed: 'Subscribed',
};

const ChannelAttributionMembers = () => {
  const [sp, setSp] = useSearchParams();
  const dispatch = useDispatch();

  // Drop one level of the node and everything under it, widening the list to the parent.
  // The node is derived from the URL, so rewriting the query re-seeds the shared filter
  // through the mount effect below — no separate state to keep in step.
  const dropLevel = (key) => {
    const next = new URLSearchParams(sp);
    const from = LEVELS.indexOf(key);
    LEVELS.slice(from).forEach((k) => next.delete(k));
    setSp(next, { replace: true });
  };
  // Variadic: the date window is two params and must go in ONE rewrite — two calls would
  // each start from the current `sp`, so the second would put the first's key back.
  const dropParam = (...keys) => {
    const next = new URLSearchParams(sp);
    keys.forEach((k) => next.delete(k));
    setSp(next, { replace: true });
  };

  const node = useMemo(() => {
    const out = {};
    NODE_KEYS.forEach((k) => { const v = sp.get(k); if (v) out[k] = v; });
    return out;
  }, [sp]);

  const genderUrl = sp.get('gender'); // 'f' | 'm' | 'all' | null
  const statusWord = node.status && node.status !== 'signups' ? STATUS_LABEL[node.status] : null;
  const crumbs = [
    ['channel', 'campaign', 'adset', 'creative'].map((k) => node[k]).filter(Boolean).join('  ›  '),
    statusWord ? `${statusWord} users` : '',
  ].filter(Boolean).join('  ·  ');
  // Re-seed whenever the node (or gender) changes.
  const sig = useMemo(() => JSON.stringify({ ...node, gender: genderUrl }), [node, genderUrl]);

  // Seed the shared filter so UserList fetches THIS node's members.
  useEffect(() => {
    const crumb = node.creative || node.adset || node.campaign || node.channel || 'All members';
    const statusWord = node.status && node.status !== 'signups' ? STATUS_LABEL[node.status] : null;
    dispatch(setStatsTypeFilter({
      source: 'attribution',
      ...node,
      // LOWERCASE on purpose: this value is also what the filter bar's Gender <Select> shows,
      // and its options are 'male'/'female'. Seeding 'Female' left the control reading
      // "All Gender" while the list was filtered to women. buildNodeMatch lowercases before
      // comparing, so the server is happy either way.
      gender: genderUrl === 'f' ? 'female' : genderUrl === 'm' ? 'male' : null,
      statsTitle: `Campaign Members · ${crumb}${statusWord ? ` · ${statusWord}` : ''}`,
      // start the node view clean of unrelated filters. statsType is nulled so a
      // stale User-Management status bucket can't ride along — the node's own
      // `status` param drives the account-status filter.
      statsType: null, searchTerm: null, country: null, state: null, city: null,
      // Pre-select the Account Status dropdown from the node. Both now speak the canonical
      // bucket keys, so the node's `status` IS the dropdown's value — except `deleted`,
      // which the dropdown cannot express (different collection) and which renders
      // DeletedAccountList below instead; leave the control blank for it.
      profileCompilation: node.status && node.status !== 'signups' && node.status !== 'deleted'
        ? node.status
        : null,
    }));
    // Pre-select the bar's Platform control from the node. It lives under its own Redux key
    // (`isIosUser`), which getUsers DELETES in attribution mode — so the control alone cannot
    // filter anything; the node's `platform` is what buildNodeMatch reads. Seeding here plus
    // the write-back below makes the select the real control for it. iOS === 'web' and
    // Android === 'app', the same mapping buildViewHref and the suffix collapse use.
    dispatch(setIosUserFilter({
      isIosUser: node.platform === 'web' ? true : node.platform === 'app' ? false : null,
    }));
    // Same for the date picker: show the window this node was opened with. `dateWant` records
    // what WE installed so the write-back below can tell it from a real user edit. When the
    // node has no window we deliberately install nothing — the picker keeps its own 30-day
    // default, and the write-back leaves the list all-time until the user actually touches it.
    if (node.from && node.to) {
      dispatch(setDateFilter({
        dateRange: { startDate: moment(node.from), endDate: moment(node.to) },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sig]);

  // Write a bar control's value BACK into the node (the URL), which re-seeds through the
  // effect above. Without this the controls would move but the list would not, because the
  // server drops their raw keys (`gender`, `createdAt`, `isIosUser`) whenever
  // source=attribution — the node params are what buildNodeMatch actually reads.
  //
  // The handshake is the whole trick. These effects run in the SAME pass as the seed, so on
  // the first run the store still holds the PREVIOUS screen's value, not the one the seed
  // just dispatched. Treating that as a user edit wrote it straight back and wiped the node
  // param the page was opened with — it silently replaced the node's window with the date
  // picker's 30-day default, and dropped `status=ghost` so the Account Status dropdown read
  // "All Statuses". So: ignore every value until the one we asked for actually arrives
  // (`settled`); only after that is a change a real edit. Re-armed on every re-seed via `sig`.
  const useNodeBinding = (storeKey, nodeValue, write, normalise = (v) => v ?? null) => {
    const want = useRef(nodeValue ?? null);
    const settled = useRef(false);
    const raw = useSelector((s) => s.filter[storeKey]);
    const value = normalise(raw);
    useEffect(() => {
      want.current = nodeValue ?? null;
      settled.current = false;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sig]);
    useEffect(() => {
      if (!settled.current) {
        // Node carries nothing for this control: adopt whatever it is showing as the
        // baseline, so an unscoped list is not narrowed by a default the user never chose.
        if (want.current === null) { want.current = value; settled.current = true; return; }
        if (value === want.current) settled.current = true;
        return;
      }
      if (value === want.current) return;
      want.current = value;
      write(value);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
  };

  const setParams = (mutate) => {
    const next = new URLSearchParams(sp);
    mutate(next);
    setSp(next, { replace: true });
  };

  // Platform: iOS === 'web', Android === 'app' — the mapping buildViewHref and the campaign
  // suffix collapse already use.
  useNodeBinding(
    'isIosUser', node.platform || null,
    (v) => setParams((q) => (v ? q.set('platform', v) : q.delete('platform'))),
    (v) => (v === true ? 'web' : v === false ? 'app' : null),
  );

  // Date window, as one `from|to` key so the pair moves together.
  useNodeBinding(
    'dateRange', node.from && node.to ? `${node.from}|${node.to}` : null,
    (v) => setParams((q) => {
      if (v) { const [s, e] = v.split('|'); q.set('from', s); q.set('to', e); }
      else { q.delete('from'); q.delete('to'); }
    }),
    (dr) => {
      const s = dr?.startDate ? moment(dr.startDate).format('YYYY-MM-DD') : null;
      const e = dr?.endDate ? moment(dr.endDate).format('YYYY-MM-DD') : null;
      return s && e ? `${s}|${e}` : null;
    },
  );

  // Account Status. Both sides speak canonical bucket keys, so it is a straight copy —
  // except `deleted`, which the dropdown cannot express (different collection, different
  // list component), so a node opened on Deleted is left alone and keeps its chip.
  useNodeBinding(
    'profileCompilation',
    node.status && node.status !== 'signups' && node.status !== 'deleted' ? node.status : null,
    (v) => { if (node.status !== 'deleted') setParams((q) => (v ? q.set('status', v) : q.delete('status'))); },
  );

  // Gender: the bar speaks 'male'/'female', the node speaks 'm'/'f'.
  useNodeBinding(
    'gender', genderUrl && genderUrl !== 'all' ? genderUrl : null,
    (v) => setParams((q) => q.set('gender', v || 'all')),
    (g) => {
      const s = String(g || '').toLowerCase();
      return s === 'female' ? 'f' : s === 'male' ? 'm' : null;
    },
  );

  // Clear the node from the shared filter on unmount (leak prevention).
  useEffect(() => () => {
    dispatch(setStatsTypeFilter({
      source: null, channel: null, campaign: null, adset: null, creative: null,
      platform: null, from: null, to: null, status: null, statsTitle: null,
    }));
    dispatch(setResetFilter());
  }, [dispatch]);

  return (
    <Box sx={{ pb: 4 }}>
      <div className="d-flex gap-16 align-items-center mb-16">
        <span>
          <img src="/assets/images/admin-panel/user-management-icon.svg" className="img-fluid" alt="" />
        </span>
        <div>
          <div className="d-flex gap-10 text-black align-items-center fw-bold font-p font-36">Campaign Members</div>
          <p>{crumbs || 'Members for the selected attribution node'}</p>
        </div>
      </div>
      {/* The node itself, as removable chips. Clearing one widens the list to its parent
          (and drops every level below it), so you can drill into a creative and then step
          back out to its campaign or channel without navigating away. Status and the
          gender/platform/date the node carried are removable the same way. */}
      {(LEVELS.some((k) => node[k]) || node.status === 'deleted') && (
        <div className="d-flex gap-8 align-items-center flex-wrap mb-16">
          <span className="font-14 text-muted">Scoped to:</span>
          {LEVELS.filter((k) => node[k]).map((k) => (
            <Chip
              key={k}
              size="small"
              label={`${LEVEL_LABEL[k]}: ${node[k]}`}
              onDelete={() => dropLevel(k)}
              sx={{ maxWidth: 320 }}
            />
          ))}
          {/* Status is the Account Status dropdown in the bar now, pre-selected from the node.
              The one exception is `deleted`, which that dropdown cannot express — those rows
              come from the delete-log and render a different list entirely — so it keeps a
              chip, and clearing it returns to the node's live members. */}
          {node.status === 'deleted' && (
            <Chip size="small" color="primary" variant="outlined"
              label={`Status: ${STATUS_LABEL.deleted}`}
              onDelete={() => dropParam('status')} />
          )}
          {/* Nothing the filter bar can express gets a chip. Gender, Platform and the date
              window are all pre-selected controls up there, so a chip would be a second
              control for the same thing. Only the attribution hierarchy and the status bucket
              have no equivalent in the bar — the bar's own status select is a different
              taxonomy (total / ghost / complete signup), not these seven buckets. */}
        </div>
      )}
      {/* The SAME filter bar User Management renders, driving the SAME shared Redux filter.
          Country/state/search narrow the list inside the node. Gender, the date range and the
          statsType select are node-owned keys, so changing them DETACHES this list from the
          tree cell it was opened from and the two will no longer reconcile — deliberate, asked
          for on 2026-09-23. Navigating back in and clicking the cell again re-seeds the node
          (the mount effect above), which is the way back to a matching count. */}
      <SearchFilter isHideUserManagement={false} />
      {node.status === 'deleted' ? <DeletedAccountList /> : <UserList showProfileColumn />}
    </Box>
  );
};

export default ChannelAttributionMembers;
